import { Button, Icon, Popover } from "@screencloud/screencloud-ui-components";
import classNames from "clsx";
import { FormattedMessage } from "react-intl";
import PlaylistItemsPreview from "src/components/Playlist/PlaylistItemsPreview";
import { Playlist } from "src/types.g";
import { Styled } from "src/components/PlaylistPicker/Item/styles";
import { TagLabelList } from "src/components/AppPicker/components/TagLabelList";
import { useAppContext } from "src/hooks/useAppContext";
import { getShareButtonContent } from "src/helpers/shareableHelper";

interface ItemProps {
  playlist: Playlist;
  isSelected: boolean;
  isOwner: boolean;
  index: number;
  callback: (playlistId: string, playlist: Playlist) => void;
  onClickTag: (tag: string) => void;
  allowSelectAll?: boolean;
}

export const Item = ({
  playlist,
  isSelected,
  isOwner,
  index,
  allowSelectAll,
  callback,
  onClickTag,
}: ItemProps) => {
  const context = useAppContext();
  const isShowShareButton = !isOwner;

  const renderShareButton = () => {
    return (
      <Popover
        inverted
        trigger={<Icon name="users" />}
        content={getShareButtonContent({
          context,
          shareable: playlist,
        })}
      />
    );
  };

  return (
    <Styled
      className={
        "media-item readonly " +
        classNames({
          selected: isSelected,
          box: allowSelectAll,
        })
      }
      key={`playlist-${playlist?.id}`}
      data-testid={`playlist-item-${index}`}
      onClick={() => callback(playlist?.id, playlist as Playlist)}
    >
      <div className="media-core">
        <div className="media-alpha">
          <div className="thumbnail-preview">
            <div
              className="wrapper"
              style={{ backgroundColor: playlist?.color! }}
            >
              <Icon name="playlist" />
            </div>
          </div>
          <div className="media-title">
            <h3 title={playlist?.name}>{playlist?.name}</h3>
            {isOwner && !playlist.draft?.isPublished && (
              <span className="pl-status">
                <Popover
                  inverted
                  position="top center"
                  content={
                    <FormattedMessage
                      id="common.text.draft_content_message"
                      defaultMessage="Changes have been made. Please publish to play the latest changes on your screens."
                    />
                  }
                  trigger={
                    <span>
                      <Icon name="warning" />
                      <FormattedMessage
                        id="common.text.draft_saved"
                        defaultMessage="Draft Saved. Publish required."
                      />
                    </span>
                  }
                />
              </span>
            )}

            <TagLabelList tags={playlist.tags} onClickHandler={onClickTag} />
          </div>
        </div>
        <div className="media-playlist-preview">
          <PlaylistItemsPreview
            playlist={
              isOwner
                ? (playlist.draft as Playlist)
                : (playlist.published as Playlist)
            }
            isInPicker={true}
            onPreviewClick={() => ""}
          />
        </div>
      </div>
      {isShowShareButton && renderShareButton()}

      {allowSelectAll ? (
        <div data-testid="playlist-check-all" className="playlist-check">
          <Button icon borderless>
            {isSelected ? (
              <Icon
                data-testid={`checkbox-checked-${index}`}
                name="checkbox-checked"
              />
            ) : (
              <Icon
                checkbox-checked={`checkbox-empty-${index}`}
                name="checkbox-empty"
              />
            )}
          </Button>
        </div>
      ) : (
        <div className="media-checked">
          <Icon name="checked-circle" />
        </div>
      )}
    </Styled>
  );
};
